import * as React from 'react';
import { useContext } from 'react';

import { Flex, Tabs, Tab, TabList, Link } from '@circleci/compass';

import { Action, LibContext, Locales } from '~/contexts/LibContext';
import { styled } from '~/styled';

import { outerTheme } from './styles';
import translations from './translations.json';

const StyledTabOption = styled('a')(({ theme }) => {
  return `
  text-decoration: none;
  color: ${theme.colors.n400};

  &:hover {
    color: ${theme.colors.n900};
  }

  &:active {
    color: ${theme.colors.n900};
  }
`;
});

const NavWrapper = styled(Flex)(({ theme }) => {
  return `
    padding: 0 ${theme.space.space16};

    @media (min-width: ${theme.breakpoints[2]}) {
      padding: 0 ${theme.space.space24};
    }
`;
});

const StyledTabList = styled(TabList)`
  > div {
    border-bottom-width: 0;
  }
`;

const NavLinksContainer = styled(Flex)(({ theme }) => {
  return `
  align-items: center;
  display: none; // by default hidden

  span:last-of-type {
    padding-right: 0;
  }

  @media (min-width: ${theme.breakpoints[2]}) {
    display: flex; // only show on desktop
  }

  `;
});

const NavLinks = styled(Link)(({ theme }) => {
  return `
  padding-right: ${theme.space.space24};
  font-family: 'Roboto';
  // overwriting both initial and hover color style
  color: ${theme.colors.n400} !important;

  &:hover {
    color: ${theme.colors.n900};
  }
`;
});

interface Props {
  activeTab?: 'home' | 'docs' | 'orbs' | 'images';
}

export const Navbar = ({ activeTab }: Props) => {
  const { notifyTrackingFired } = useContext(LibContext);
  const locale = 'en';

  // Compass tab will automatically select the first tab if activeTab is not supplied.
  // providing an empty string will force compass to select nothing in this case.
  const selectedTabId = activeTab ?? '';

  return (
    <Flex
      minHeight={outerTheme.globals.navHeight}
      grow={1}
      direction={'column'}
      data-testid="header-navbar"
    >
      <NavWrapper direction={'row'} justify={'space-between'}>
        {/* Header Nav tabs */}
        <Tabs selectedId={selectedTabId}>
          <StyledTabList id="navbar-tablist" aria-label="Navigation Tabs">
            {translations[locale as Locales].navbarTabs.map((tab, index) => {
              return (
                <StyledTabOption
                  key={index}
                  href={tab.href}
                  onClick={
                    notifyTrackingFired
                      ? () => {
                          return notifyTrackingFired(
                            'navbarTab',
                            Action.clicked,
                            {
                              tab: tab.text,
                              href: tab.href,
                              location: 'header',
                            },
                          );
                        }
                      : undefined
                  }
                >
                  <Tab id={tab.id}>{tab.text}</Tab>
                </StyledTabOption>
              );
            })}
          </StyledTabList>
        </Tabs>
        {/* Header Nav Links */}
        <NavLinksContainer>
          {translations[locale as Locales].navbarLinks.map((link, index) => {
            return (
              <NavLinks key={index} href={link.href}>
                {link.text}
              </NavLinks>
            );
          })}
        </NavLinksContainer>
      </NavWrapper>
    </Flex>
  );
};
